import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Upload, Search, RefreshCw, Plus, ArrowDownLeft, ArrowUpRight, Replace, FileText, History, Smartphone, Edit, Trash2, MoreVertical, AlertCircle, RotateCcw, Download } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';

const STATUS_COLORS = {
  Active: 'bg-green-50 text-green-700 border-green-300',
  Available: 'bg-slate-100 text-slate-700 border-slate-300',
  Deactivated: 'bg-rose-50 text-rose-700 border-rose-300',
};

const PLATFORM_OPTIONS = ['Talabat', 'Careem', 'Keeta', 'Noon', 'Deliveroo', 'Smart City', 'Other'];

const nowParts = () => {
  const t = new Date();
  return {
    date: `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`,
    time: `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`,
  };
};

const SimCards = ({ user }) => {
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [riders, setRiders] = useState([]);
  const [meta, setMeta] = useState({ service_providers: [], statuses: [] });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');

  // Add SIM dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ mobile_number: '', service_provider: '', monthly_plan: '', status: 'Available', sim_type: '', owner: '' });
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Handover / Takeover / Replace dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', sim: null });
  const [actionForm, setActionForm] = useState({ rider_id: '', platform: '', reason: '', transaction_date: '', transaction_time: '' });
  const [actionSubmitting, setActionSubmitting] = useState(false);

  // History dialog
  const [historyDialog, setHistoryDialog] = useState({ open: false, sim: null, rows: [], loading: false });

  // Excel upload — preview + apply. Duplicate mobile numbers are always
  // rejected (never overwritten) so no mode toggle is needed.
  const [uploadDialog, setUploadDialog] = useState({ open: false, file: null, preview: null, loading: false });

  // Rider mobile sync
  const [syncDialog, setSyncDialog] = useState({ open: false, loading: false, data: null, selected: [] });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (providerFilter) params.append('service_provider', providerFilter);
      const [sRes, rRes, mRes] = await Promise.all([
        axios.get(`${API}/inventory/sim-cards?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/inventory/sim-cards/meta`, { headers: getAuthHeader() }),
      ]);
      setSims(sRes.data || []);
      setRiders(rRes.data || []);
      setMeta(mRes.data || { service_providers: [], statuses: [] });
    } catch (e) {
      toast.error('Failed to load SIM cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  // Derived metrics — 3-state status model (Active / Available / Deactivated).
  // Any legacy value coming from the API is already normalized server-side.
  const metrics = useMemo(() => {
    const active = sims.filter(s => s.status === 'Active').length;
    const available = sims.filter(s => s.status === 'Available').length;
    const deactivated = sims.filter(s => s.status === 'Deactivated').length;
    return { total: sims.length, active, available, deactivated };
  }, [sims]);

  const riderOptions = useMemo(
    () => riders.map(r => ({
      value: r.id,
      label: `${r.name}${r.rider_unique_id ? ' · ' + r.rider_unique_id : ''}${r.platform ? ' · ' + r.platform : ''}`,
      raw: r,
    })),
    [riders]
  );

  // ----- Add SIM -----
  const handleAddSim = async () => {
    if (!addForm.mobile_number.trim()) { toast.error('Mobile number is required'); return; }
    setAddSubmitting(true);
    try {
      await axios.post(`${API}/inventory/sim-cards`, addForm, { headers: getAuthHeader() });
      toast.success('SIM added to inventory');
      setAddOpen(false);
      setAddForm({ mobile_number: '', service_provider: '', monthly_plan: '', status: 'Available', sim_type: '', owner: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add SIM');
    } finally {
      setAddSubmitting(false);
    }
  };

  // ----- Action dialog (Handover / Takeover / Replace) -----
  const openAction = (type, sim) => {
    const np = nowParts();
    setActionDialog({ open: true, type, sim });
    setActionForm({
      rider_id: type === 'Handover' ? '' : (sim.rider_id || ''),
      platform: sim.platform || '',
      reason: '',
      transaction_date: np.date,
      transaction_time: np.time,
    });
  };

  const submitAction = async () => {
    const { type, sim } = actionDialog;
    if (!actionForm.reason || actionForm.reason.trim().length < 3) { toast.error('Reason is required (min 3 chars)'); return; }
    if (type === 'Handover' && !actionForm.rider_id) { toast.error('Select a rider'); return; }
    setActionSubmitting(true);
    try {
      const endpoint = type.toLowerCase();
      const body = {
        rider_id: actionForm.rider_id,
        platform: actionForm.platform,
        reason: actionForm.reason.trim(),
        transaction_date: actionForm.transaction_date,
        transaction_time: actionForm.transaction_time,
      };
      await axios.post(`${API}/inventory/sim-cards/${sim.id}/${endpoint}`, body, { headers: getAuthHeader() });
      toast.success(`${type} recorded successfully`);
      setActionDialog({ open: false, type: '', sim: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${type} failed`);
    } finally {
      setActionSubmitting(false);
    }
  };

  // ----- History -----
  const openHistory = async (sim) => {
    setHistoryDialog({ open: true, sim, rows: [], loading: true });
    try {
      const res = await axios.get(`${API}/inventory/sim-cards/${sim.id}/transactions`, { headers: getAuthHeader() });
      setHistoryDialog({ open: true, sim, rows: res.data || [], loading: false });
    } catch (e) {
      toast.error('Failed to load transaction history');
      setHistoryDialog({ open: false, sim: null, rows: [], loading: false });
    }
  };

  // ----- Excel upload -----
  const onExcelSelected = async (file) => {
    if (!file) return;
    setUploadDialog({ open: true, file, preview: null, loading: true });
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/inventory/sim-cards/upload-excel/preview`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setUploadDialog({ open: true, file, preview: res.data, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse Excel');
      setUploadDialog({ open: false, file: null, preview: null, loading: false });
    }
  };

  const applyUpload = async () => {
    const rows = uploadDialog.preview?.rows || [];
    setUploadDialog(d => ({ ...d, loading: true }));
    try {
      const res = await axios.post(`${API}/inventory/sim-cards/upload-excel/apply`, { rows }, { headers: getAuthHeader() });
      const { inserted = 0, rejected = 0, rejected_rows = [] } = res.data;
      if (rejected > 0) {
        // Group rejected rows by their actual reason so the toast shows the
        // real story instead of a hardcoded "duplicate" message.
        const buckets = {};
        for (const rr of rejected_rows) {
          const key = rr.reason || 'Unknown reason';
          if (!buckets[key]) buckets[key] = [];
          buckets[key].push(rr);
        }
        const parts = Object.entries(buckets).map(([reason, list]) => {
          const short = reason.length > 90 ? reason.slice(0, 87) + '…' : reason;
          return `• ${list.length} row(s): ${short}`;
        });
        toast.warning(
          `Inserted ${inserted} SIM(s). Rejected ${rejected}:\n${parts.join('\n')}`,
          { duration: 12000 }
        );
        // Also log full details (all mobile numbers + reasons) to the console
        // for easy copy/paste when troubleshooting a stubborn row.
        // eslint-disable-next-line no-console
        console.warn('SIM bulk upload — rejected rows breakdown:', buckets);
      } else {
        toast.success(`Inserted ${inserted} new SIM(s). No rows rejected.`);
      }
      setUploadDialog({ open: false, file: null, preview: null, loading: false });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply');
      setUploadDialog(d => ({ ...d, loading: false }));
    }
  };

  // ----- Sync rider mobiles -----
  const openSync = async () => {
    setSyncDialog({ open: true, loading: true, data: null, selected: [] });
    try {
      const res = await axios.get(`${API}/inventory/sim-cards/sync-rider-mobile/preview`, { headers: getAuthHeader() });
      setSyncDialog({ open: true, loading: false, data: res.data, selected: (res.data.mismatches || []).map(m => m.rider_id) });
    } catch (e) {
      toast.error('Failed to load sync preview');
      setSyncDialog({ open: false, loading: false, data: null, selected: [] });
    }
  };

  const applySync = async () => {
    if (!syncDialog.selected.length) { toast.error('Select at least one rider'); return; }
    setSyncDialog(d => ({ ...d, loading: true }));
    try {
      const res = await axios.post(`${API}/inventory/sim-cards/sync-rider-mobile/apply`, { rider_ids: syncDialog.selected }, { headers: getAuthHeader() });
      toast.success(`${res.data.updated} rider mobile number(s) updated`);
      setSyncDialog({ open: false, loading: false, data: null, selected: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
      setSyncDialog(d => ({ ...d, loading: false }));
    }
  };

  const handleDelete = async (sim) => {
    if (!window.confirm(`Delete SIM ${sim.mobile_number}? Transaction history will also be removed.`)) return;
    try {
      await axios.delete(`${API}/inventory/sim-cards/${sim.id}`, { headers: getAuthHeader() });
      toast.success('SIM deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // ----- Deactivate / Reactivate -----
  const handleDeactivate = async (sim) => {
    if (sim.status !== 'Available') {
      toast.error('Only Available SIMs can be deactivated. Take the SIM back from the rider first.');
      return;
    }
    const reason = window.prompt(`Deactivate SIM ${sim.mobile_number}?\nOptional reason (e.g. cancelled at Etisalat):`, '');
    if (reason === null) return;  // user cancelled
    try {
      await axios.post(`${API}/inventory/sim-cards/${sim.id}/deactivate`, { reason }, { headers: getAuthHeader() });
      toast.success(`SIM ${sim.mobile_number} deactivated`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate SIM');
    }
  };

  const handleReactivate = async (sim) => {
    if (sim.status !== 'Deactivated') {
      toast.error('Only Deactivated SIMs can be reactivated.');
      return;
    }
    if (!window.confirm(`Reactivate SIM ${sim.mobile_number}? It will move back to the Available pool.`)) return;
    try {
      await axios.post(`${API}/inventory/sim-cards/${sim.id}/reactivate`, {}, { headers: getAuthHeader() });
      toast.success(`SIM ${sim.mobile_number} reactivated`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate SIM');
    }
  };

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total SIMs', val: metrics.total, color: 'bg-blue-50 border-blue-200 text-blue-700' },
          { label: 'Active', val: metrics.active, color: 'bg-green-50 border-green-200 text-green-700' },
          { label: 'Available', val: metrics.available, color: 'bg-slate-50 border-slate-200 text-slate-700' },
          { label: 'Deactivated', val: metrics.deactivated, color: 'bg-rose-50 border-rose-200 text-rose-700' },
        ].map(k => (
          <div key={k.label} className={`rounded-lg border ${k.color} px-4 py-3`}>
            <p className="text-[11px] uppercase tracking-wide font-medium">{k.label}</p>
            <p className="text-2xl font-bold mt-0.5">{k.val}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchData()} placeholder="Search mobile, rider, plan…" className="pl-8 h-9" data-testid="sim-search" />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-36 h-9" data-testid="sim-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {(meta.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={providerFilter} onValueChange={(v) => { setProviderFilter(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-44 h-9" data-testid="sim-filter-provider"><SelectValue placeholder="Provider" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {(meta.service_providers || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-9" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>

            <div className="ml-auto flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-9 bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 border-blue-600"
                onClick={() => setUploadDialog({ open: true, file: null, preview: null, loading: false })}
                data-testid="sim-upload-excel-btn"
              >
                <Upload className="w-4 h-4 mr-1" />Upload Excel
              </Button>
              <Button size="sm" variant="outline" className="h-9" onClick={openSync} data-testid="sim-sync-rider">
                <RotateCcw className="w-4 h-4 mr-1" />Sync Rider Mobiles
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                onClick={async () => {
                  if (!window.confirm('Mark ALL SIM rows that currently have no Owner as Company / GEDS?\n\nThis is a one-time backfill and is safe to run multiple times.')) return;
                  try {
                    const res = await axios.post(`${API}/inventory/sim-cards/backfill-owner-geds`, {}, { headers: getAuthHeader() });
                    toast.success(res.data.message);
                    fetchData();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Backfill failed');
                  }
                }}
                data-testid="sim-mark-geds"
              >
                Mark all as GEDS
              </Button>
              <Button size="sm" className="h-9 bg-lime-600 hover:bg-lime-700" onClick={() => setAddOpen(true)} data-testid="sim-add-btn">
                <Plus className="w-4 h-4 mr-1" />Add SIM
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Smartphone className="w-5 h-5 text-blue-600" />SIM Card Inventory ({sims.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          ) : sims.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">No SIM cards yet. Click <strong>Add SIM</strong> or <strong>Upload Excel</strong> to begin.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs whitespace-nowrap w-[140px] min-w-[140px]">Mobile Number</TableHead>
                    <TableHead className="text-xs">Rider</TableHead>
                    <TableHead className="text-xs">Platform</TableHead>
                    <TableHead className="text-xs">Owner</TableHead>
                    <TableHead className="text-xs">Plan</TableHead>
                    <TableHead className="text-xs">Provider</TableHead>
                    <TableHead className="text-xs">Handover Date</TableHead>
                    <TableHead className="text-xs text-center">Status</TableHead>
                    <TableHead className="text-xs text-center">Billing</TableHead>
                    <TableHead className="text-xs text-center">History</TableHead>
                    <TableHead className="text-xs text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sims.map(s => (
                    <TableRow key={s.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-xs font-medium whitespace-nowrap">{s.mobile_number}</TableCell>
                      <TableCell className="text-xs">
                        {s.status === 'Active' && s.rider_name ? (
                          <div>
                            <div className="font-medium">{s.rider_name}</div>
                            {s.rider_unique_id && <div className="text-[10px] text-slate-500">{s.rider_unique_id}</div>}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">{s.status === 'Active' && s.platform ? s.platform : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs">
                        {s.owner ? (
                          <div className="flex flex-col leading-tight">
                            <span className="font-medium">{s.owner}</span>
                            <span className="text-[10px] text-slate-500 uppercase">{s.sim_type || ''}</span>
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">{s.monthly_plan || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs">{s.service_provider || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{s.status === 'Active' && s.date_of_handover ? s.date_of_handover : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] text-blue-600" onClick={() => toast.info('Monthly bill upload — coming soon')}>
                          <FileText className="w-3.5 h-3.5 mr-1" />View
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] text-purple-600" onClick={() => openHistory(s)} data-testid={`sim-history-${s.id}`}>
                          <History className="w-3.5 h-3.5 mr-1" />View
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {s.status === 'Available' && (
                              <DropdownMenuItem onClick={() => openAction('Handover', s)} data-testid={`sim-handover-${s.id}`}>
                                <ArrowUpRight className="w-4 h-4 mr-2 text-green-600" />Handover
                              </DropdownMenuItem>
                            )}
                            {s.status === 'Active' && (
                              <DropdownMenuItem onClick={() => openAction('Takeover', s)} data-testid={`sim-takeover-${s.id}`}>
                                <ArrowDownLeft className="w-4 h-4 mr-2 text-amber-600" />Takeover
                              </DropdownMenuItem>
                            )}
                            {s.status === 'Available' && (
                              <DropdownMenuItem onClick={() => handleDeactivate(s)} data-testid={`sim-deactivate-${s.id}`}>
                                <AlertCircle className="w-4 h-4 mr-2 text-rose-600" />Deactivate
                              </DropdownMenuItem>
                            )}
                            {s.status === 'Deactivated' && (
                              <DropdownMenuItem onClick={() => handleReactivate(s)} data-testid={`sim-reactivate-${s.id}`}>
                                <RotateCcw className="w-4 h-4 mr-2 text-emerald-600" />Reactivate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openAction('Replace', s)} data-testid={`sim-replace-${s.id}`}>
                              <Replace className="w-4 h-4 mr-2 text-blue-600" />Replace SIM
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(s)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Add SIM Dialog ===== */}
      <Dialog open={addOpen} onOpenChange={(o) => !addSubmitting && setAddOpen(o)}>
        <DialogContent className="max-w-md" data-testid="sim-add-dialog">
          <DialogHeader>
            <DialogTitle>Add SIM Card</DialogTitle>
            <DialogDescription>Add a new SIM to inventory. Mark as Active or In Store.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase">Mobile Number <span className="text-red-500">*</span></Label>
              <Input value={addForm.mobile_number} onChange={(e) => setAddForm({ ...addForm, mobile_number: e.target.value })} placeholder="+971 50 123 4567" data-testid="sim-add-mobile" />
            </div>
            <div>
              <Label className="text-xs uppercase">Service Provider</Label>
              <Select value={addForm.service_provider || ''} onValueChange={(v) => setAddForm({ ...addForm, service_provider: v })}>
                <SelectTrigger data-testid="sim-add-provider"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>
                  {(meta.service_providers || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Monthly Plan</Label>
              <Input value={addForm.monthly_plan} onChange={(e) => setAddForm({ ...addForm, monthly_plan: e.target.value })} placeholder="e.g. Smart 199" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">SIM Type</Label>
                <Select value={addForm.sim_type || ''} onValueChange={(v) => setAddForm({ ...addForm, sim_type: v, owner: '' })}>
                  <SelectTrigger data-testid="sim-add-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company SIM card</SelectItem>
                    <SelectItem value="platform">Platform SIM card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase">Owner</Label>
                <Select
                  value={addForm.owner || ''}
                  onValueChange={(v) => setAddForm({ ...addForm, owner: v })}
                  disabled={!addForm.sim_type}
                >
                  <SelectTrigger data-testid="sim-add-owner">
                    <SelectValue placeholder={addForm.sim_type ? 'Select owner' : 'Choose SIM type first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(((meta.owners_by_type || {})[addForm.sim_type]) || []).map(o => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase">Initial Status</Label>
              <Select value={addForm.status} onValueChange={(v) => setAddForm({ ...addForm, status: v })}>
                <SelectTrigger data-testid="sim-add-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">SIMs start as <strong>Available</strong> and turn <strong>Active</strong> automatically when handed over to a rider or staff. Use the row menu to Deactivate a SIM at the provider.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSubmitting}>Cancel</Button>
              <Button onClick={handleAddSim} disabled={addSubmitting} className="bg-lime-600 hover:bg-lime-700" data-testid="sim-add-submit">{addSubmitting ? 'Saving…' : 'Add SIM'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Action Dialog (Handover / Takeover / Replace) ===== */}
      <Dialog open={actionDialog.open} onOpenChange={(o) => !actionSubmitting && setActionDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg" data-testid={`sim-action-dialog-${actionDialog.type?.toLowerCase()}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog.type === 'Handover' && <ArrowUpRight className="w-5 h-5 text-green-600" />}
              {actionDialog.type === 'Takeover' && <ArrowDownLeft className="w-5 h-5 text-amber-600" />}
              {actionDialog.type === 'Replace' && <Replace className="w-5 h-5 text-blue-600" />}
              {actionDialog.type} SIM — {actionDialog.sim?.mobile_number}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'Replace' && 'Physical SIM is being replaced; the mobile number stays the same.'}
              {actionDialog.type === 'Handover' && 'Assign this SIM to a rider. Status will change to Active.'}
              {actionDialog.type === 'Takeover' && 'Take the SIM back from the rider. Status will change to Inactive.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase">Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={actionForm.transaction_date} onChange={(e) => setActionForm({ ...actionForm, transaction_date: e.target.value })} data-testid="sim-action-date" />
              </div>
              <div>
                <Label className="text-xs uppercase">Time <span className="text-red-500">*</span></Label>
                <Input type="time" value={actionForm.transaction_time} onChange={(e) => setActionForm({ ...actionForm, transaction_time: e.target.value })} data-testid="sim-action-time" />
              </div>
            </div>

            {actionDialog.type === 'Handover' && (
              <>
                <div>
                  <Label className="text-xs uppercase">Rider <span className="text-red-500">*</span></Label>
                  <div data-testid="sim-action-rider">
                    <SearchableSelect
                      value={actionForm.rider_id}
                      onValueChange={(v) => {
                        const r = riders.find(x => x.id === v);
                        setActionForm(f => ({ ...f, rider_id: v, platform: r?.platform || f.platform }));
                      }}
                      options={riderOptions}
                      placeholder="Search rider by name or ID…"
                      data-testid="sim-action-rider-select"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase">Platform</Label>
                  <Select value={actionForm.platform || ''} onValueChange={(v) => setActionForm({ ...actionForm, platform: v })}>
                    <SelectTrigger><SelectValue placeholder="Auto-filled from rider" /></SelectTrigger>
                    <SelectContent>
                      {PLATFORM_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {(actionDialog.type === 'Takeover' || actionDialog.type === 'Replace') && (
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-xs space-y-0.5">
                <div><span className="text-slate-500">Current rider:</span> <strong>{actionDialog.sim?.rider_name || '—'}</strong> {actionDialog.sim?.rider_unique_id && <span className="text-slate-400">({actionDialog.sim?.rider_unique_id})</span>}</div>
                <div><span className="text-slate-500">Platform:</span> {actionDialog.sim?.platform || '—'}</div>
                <div><span className="text-slate-500">Plan:</span> {actionDialog.sim?.monthly_plan || '—'} · <span className="text-slate-500">Provider:</span> {actionDialog.sim?.service_provider || '—'}</div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase">Reason <span className="text-red-500">*</span></Label>
              <Textarea rows={3} value={actionForm.reason} onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })} placeholder={
                actionDialog.type === 'Handover' ? 'Why is this SIM being handed over to this rider?' :
                actionDialog.type === 'Takeover' ? 'Why is the SIM being taken back?' :
                'Why is the physical SIM being replaced?'
              } data-testid="sim-action-reason" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setActionDialog({ open: false, type: '', sim: null })} disabled={actionSubmitting}>Cancel</Button>
              <Button onClick={submitAction} disabled={actionSubmitting} className="bg-lime-600 hover:bg-lime-700" data-testid="sim-action-submit">{actionSubmitting ? 'Saving…' : `Confirm ${actionDialog.type}`}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== History Dialog ===== */}
      <Dialog open={historyDialog.open} onOpenChange={(o) => !o && setHistoryDialog({ open: false, sim: null, rows: [], loading: false })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-purple-600" />Transaction History — {historyDialog.sim?.mobile_number}</DialogTitle>
            <DialogDescription>Handover, takeover and replacement events for this SIM, newest first.</DialogDescription>
          </DialogHeader>
          {historyDialog.loading ? (
            <div className="text-center py-6 text-sm text-slate-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          ) : historyDialog.rows.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500">No transactions yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Date / Time</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Assigned To</TableHead>
                  <TableHead className="text-xs">Platform / Role</TableHead>
                  <TableHead className="text-xs">Reason</TableHead>
                  <TableHead className="text-xs">By</TableHead>
                  <TableHead className="text-xs text-center">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyDialog.rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs font-mono">{r.transaction_date} {r.transaction_time}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className={
                        r.transaction_type === 'Handover' ? 'border-green-300 text-green-700' :
                        r.transaction_type === 'Takeover' ? 'border-amber-300 text-amber-700' :
                        'border-blue-300 text-blue-700'
                      }>{r.transaction_type}</Badge>
                      {r.assignee_type === 'staff' && (
                        <Badge variant="outline" className="ml-1 text-[10px] border-purple-300 text-purple-700">Staff</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.rider_name || '—'} {r.rider_unique_id && <span className="text-[10px] text-slate-400">({r.rider_unique_id})</span>}</TableCell>
                    <TableCell className="text-xs">{r.platform || '—'}</TableCell>
                    <TableCell className="text-xs max-w-[260px]">{r.reason || r.notes || '—'}</TableCell>
                    <TableCell className="text-[10px] text-slate-500">{r.created_by || 'system'}</TableCell>
                    <TableCell className="text-center">
                      {(r.transaction_type === 'Handover' || r.transaction_type === 'Takeover') ? (
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 text-[11px] text-blue-600"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const resp = await fetch(
                                `${API}/inventory/sim-cards/${historyDialog.sim.id}/transactions/${r.id}/pdf`,
                                { headers: { Authorization: `Bearer ${token}` } },
                              );
                              if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                              const blob = await resp.blob();
                              const url = window.URL.createObjectURL(blob);
                              const cd = resp.headers.get('content-disposition') || '';
                              const nm = (cd.match(/filename="?([^";]+)"?/i) || [])[1] || `sim_${r.transaction_type}.pdf`;
                              const a = document.createElement('a');
                              a.href = url; a.download = nm;
                              document.body.appendChild(a); a.click(); a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (e) {
                              toast.error('Failed to download PDF');
                            }
                          }}
                          data-testid={`sim-txn-pdf-${r.id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />PDF
                        </Button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Excel Upload Dialog ===== */}
      <Dialog open={uploadDialog.open} onOpenChange={(o) => !uploadDialog.loading && setUploadDialog({ open: o, file: null, preview: null, loading: false })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sim-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-blue-600" />Upload SIM Card Inventory</DialogTitle>
            <DialogDescription>
              Upload a bulk Excel file to import SIM cards. Need a starting point? <strong>Download the template</strong> below.
            </DialogDescription>
          </DialogHeader>

          {/* Format hint */}
          {!uploadDialog.preview && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Expected File Format:</p>
                  <ul className="list-disc list-inside mt-1 text-xs space-y-0.5">
                    <li>Excel file (.xls or .xlsx)</li>
                    <li>Columns: <em>Mobile Number, Rider ID, Rider Name, Date of Handover, Monthly Plan, Service Provider</em></li>
                    <li>Rider ID accepts BOTH rider IDs (e.g. <code>GEDS0187</code>, <code>EZYM0003</code>) AND staff IDs (e.g. <code>STAF0014</code>) — the system routes each to the correct person automatically</li>
                    <li>Service Provider must be one of: Etisalat, du, Virgin Mobile</li>
                    <li>Duplicates (by mobile number) will be flagged — you choose to skip or update</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* File picker + Template download */}
          {!uploadDialog.preview && !uploadDialog.loading && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase">Select Excel File</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => onExcelSelected(e.target.files?.[0])}
                  className="cursor-pointer mt-1"
                  data-testid="sim-upload-file-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">Max 25 MB</p>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                data-testid="sim-template-download"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/inventory/sim-cards/upload-excel/template`, {
                      headers: getAuthHeader(),
                      responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'sim_card_inventory_template.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Template downloaded');
                  } catch (e) {
                    toast.error('Failed to download template');
                  }
                }}
              >
                <Download className="w-4 h-4" />
                Download Template
              </Button>

              <div className="flex justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => setUploadDialog({ open: false, file: null, preview: null, loading: false })}>Cancel</Button>
              </div>
            </div>
          )}

          {uploadDialog.loading && !uploadDialog.preview ? (
            <div className="text-center py-8 text-sm text-slate-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Parsing Excel…</div>
          ) : uploadDialog.preview && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2">
                  <p className="text-[10px] uppercase font-medium text-blue-700">Total Rows</p>
                  <p className="text-xl font-bold">{uploadDialog.preview.rows.length}</p>
                </div>
                <div className="rounded border border-green-200 bg-green-50 px-3 py-2">
                  <p className="text-[10px] uppercase font-medium text-green-700">New</p>
                  <p className="text-xl font-bold">{uploadDialog.preview.new_rows.length}</p>
                </div>
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-[10px] uppercase font-medium text-amber-700">Duplicates Found</p>
                  <p className="text-xl font-bold">{uploadDialog.preview.duplicates.length}</p>
                </div>
              </div>

              {uploadDialog.preview.duplicates.length > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-3 space-y-1">
                  <p className="text-xs font-semibold text-red-800 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {uploadDialog.preview.duplicates.length} duplicate mobile number(s) will be REJECTED</p>
                  <p className="text-[11px] text-red-700 leading-snug">
                    Duplicate rows are never overwritten. Only the new rows below will be added to the SIM Card Inventory. To modify an existing SIM, edit it directly on the inventory page instead.
                  </p>
                </div>
              )}

              <div className="max-h-[40vh] overflow-y-auto border rounded">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-100">
                    <TableRow>
                      <TableHead className="text-xs">Row</TableHead>
                      <TableHead className="text-xs">Mobile</TableHead>
                      <TableHead className="text-xs">Rider</TableHead>
                      <TableHead className="text-xs">Plan</TableHead>
                      <TableHead className="text-xs">Provider</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploadDialog.preview.rows.map(r => (
                      <TableRow key={r.row_index}>
                        <TableCell className="text-[10px]">{r.row_index}</TableCell>
                        <TableCell className="text-xs font-mono">{r.mobile_number}</TableCell>
                        <TableCell className="text-xs">{r.rider_name || <span className="text-slate-400">unresolved</span>}</TableCell>
                        <TableCell className="text-xs">{r.monthly_plan || '—'}</TableCell>
                        <TableCell className="text-xs">{r.service_provider || '—'} {r.service_provider_warning && <span className="text-amber-600 text-[10px] ml-1" title={r.service_provider_warning}>⚠</span>}</TableCell>
                        <TableCell>
                          {r.existing_id ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">Duplicate</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-[10px]">New</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setUploadDialog({ open: false, file: null, preview: null, loading: false })} disabled={uploadDialog.loading}>Cancel</Button>
                <Button onClick={applyUpload} disabled={uploadDialog.loading} className="bg-lime-600 hover:bg-lime-700" data-testid="upload-apply-btn">{uploadDialog.loading ? 'Applying…' : 'Apply'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Sync Rider Mobile Dialog ===== */}
      <Dialog open={syncDialog.open} onOpenChange={(o) => !syncDialog.loading && setSyncDialog({ open: o, loading: false, data: null, selected: [] })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sim-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-5 h-5 text-blue-600" />Sync Rider Mobile Numbers</DialogTitle>
            <DialogDescription>Only riders under <strong>in-house lessees</strong> are compared. Pick which rider records should adopt the mobile number recorded in SIM inventory.</DialogDescription>
          </DialogHeader>
          {syncDialog.loading && !syncDialog.data ? (
            <div className="text-center py-8 text-sm text-slate-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Checking…</div>
          ) : syncDialog.data && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded border border-green-200 bg-green-50 px-3 py-2">
                  <p className="text-[10px] uppercase font-medium text-green-700">Matching</p>
                  <p className="text-xl font-bold">{syncDialog.data.matched_count}</p>
                </div>
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-[10px] uppercase font-medium text-amber-700">Mismatches</p>
                  <p className="text-xl font-bold">{syncDialog.data.mismatches.length}</p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[10px] uppercase font-medium text-slate-600">Riders w/o SIM</p>
                  <p className="text-xl font-bold">{syncDialog.data.no_sim_count}</p>
                </div>
              </div>

              {syncDialog.data.mismatches.length === 0 ? (
                <div className="rounded border border-green-200 bg-green-50 p-4 text-center text-sm text-green-800">
                  All in-house rider mobile numbers are in sync with SIM inventory. ✓
                </div>
              ) : (
                <>
                  <div className="max-h-[45vh] overflow-y-auto border rounded">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-100">
                        <TableRow>
                          <TableHead className="text-xs w-8">
                            <input
                              type="checkbox"
                              checked={syncDialog.selected.length === syncDialog.data.mismatches.length}
                              onChange={(e) => setSyncDialog(d => ({ ...d, selected: e.target.checked ? d.data.mismatches.map(m => m.rider_id) : [] }))}
                            />
                          </TableHead>
                          <TableHead className="text-xs">Rider</TableHead>
                          <TableHead className="text-xs">Lessee</TableHead>
                          <TableHead className="text-xs">Rider Page Mobile</TableHead>
                          <TableHead className="text-xs">SIM Inventory Mobile</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncDialog.data.mismatches.map(m => (
                          <TableRow key={m.rider_id} className="hover:bg-slate-50">
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={syncDialog.selected.includes(m.rider_id)}
                                onChange={(e) => setSyncDialog(d => ({ ...d, selected: e.target.checked ? [...d.selected, m.rider_id] : d.selected.filter(x => x !== m.rider_id) }))}
                              />
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="font-medium">{m.rider_name}</div>
                              <div className="text-[10px] text-slate-500">{m.rider_unique_id}</div>
                            </TableCell>
                            <TableCell className="text-xs">{m.lessee_name}</TableCell>
                            <TableCell className="text-xs font-mono text-red-600">{m.rider_mobile_current || '(empty)'}</TableCell>
                            <TableCell className="text-xs font-mono text-green-700">{m.sim_mobile}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-[11px] text-slate-500">Selected rider records will be updated to use the SIM Inventory mobile number.</p>
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="outline" onClick={() => setSyncDialog({ open: false, loading: false, data: null, selected: [] })} disabled={syncDialog.loading}>Cancel</Button>
                    <Button onClick={applySync} disabled={syncDialog.loading || !syncDialog.selected.length} className="bg-blue-600 hover:bg-blue-700" data-testid="sync-apply-btn">
                      {syncDialog.loading ? 'Updating…' : `Update ${syncDialog.selected.length} Rider(s)`}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimCards;
